/**
 * Persistent on-device audio cache (IndexedDB) — the runtime half of the
 * "compute once, cache everywhere" plan (docs/tts-dynamic-audio-architecture.md).
 *
 * Cloud-synthesized lines are stored here keyed by ttsAudioKey, so any line heard
 * once plays HD forever — offline included — and each unique line is synthesized
 * at most once per device (large cloud-cost reduction). This is what makes
 * dynamic/daily/parent-supplied content scale without a quality gap.
 *
 * Design notes:
 * - Generic keyed blob store: speechService computes the key; this module just
 *   persists/serves the blob with LRU eviction. Voice-agnostic.
 * - An in-memory key Set mirrors the DB so membership/availability checks are
 *   SYNC (the speak() tier gate is sync) while blob reads stay async.
 * - Degrades to a silent no-op when IndexedDB is unavailable (unit tests / SSR),
 *   so callers never need to guard.
 */
const DB_NAME = 'lwa-audio-cache';
const STORE = 'clips';
const DB_VERSION = 1;
/* Soft cap; LRU-evict down to ~90% when exceeded.
 *
 * Raised 350 MB → 2 GB for App-Size architecture v2 (docs/app-size-architecture-v2.md).
 * v2 moves the app from "bundle everything" (a 5.8 GB floor that blocked the deploy)
 * to a lean ~930 MB bundled floor plus whole-LEVEL downloads, so this cache is now
 * where a child's actual level lives — and levels are big: measured 221 MB–1.5 GB
 * today, up to ~2.2 GB for the largest grade once book narration is baked. At 350 MB
 * a single level could never be held, so downloading one would thrash: fetch, evict,
 * re-fetch, and the child would be back on un-cached lines (the never-robotic rule
 * depends on this cache actually retaining what it downloaded).
 *
 * 2 GB is chosen to hold a level's LESSON narration many times over plus a healthy
 * share of its books, while staying well inside the storage a phone can spare. It is
 * deliberately NOT sized to hold a whole 2.2 GB book set: ensureLevel downloads
 * lessons FIRST and pins them, so what an over-budget device sheds is book series
 * (each independently re-downloadable, all-or-nothing, and gated in the UI) — never
 * the lesson audio a child is mid-lesson on. */
const MAX_BYTES = 2 * 1024 * 1024 * 1024;
/** Clips untouched for this long are swept on startup (spec §6 "auto-remove packs
 *  untouched for N days"). Pins are respected — an explicitly kept level survives. */
const STALE_CLIP_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;   // 60 days

interface ClipRecord {
  blob: Blob;
  size: number;
  lastUsed: number;
}

let _db: IDBDatabase | null = null;
let _ready: Promise<void> | null = null;
const _keys = new Set<string>(); // in-memory mirror for sync membership checks
let _bytes = 0;
// Monotonic mutation counter — bumped whenever the key set changes (init load,
// put, delete). Lets callers MEMOIZE derived counts (e.g. contentPackService's
// per-pack downloaded-count, polled every 700ms across 13+ level rows) and
// recompute only when the cache actually changed, instead of rescanning ~31k-key
// sets on every poll tick (the download-screen CPU burn).
let _revision = 0;
// PINNED keys are exempt from LRU eviction — the orchestrated content-pack
// prefetch (Phase 2) pins the child's current grade ±1 + the offline floor so a
// least-recently-used sweep can never silently evict the very clips the child is
// about to need (which would drop those lines to the robotic OS voice). Pinning
// is purely additive: when nothing is pinned, prune() behaves exactly as before.
const _pinned = new Set<string>();

function idbFactory(): IDBFactory | null {
  try {
    return typeof indexedDB !== 'undefined' ? indexedDB : null;
  } catch {
    return null;
  }
}

/**
 * Open the cache DB and load the key set + total size into memory. Idempotent and
 * safe to call repeatedly. Resolves (never rejects) even when IndexedDB is absent.
 */
export function initAudioCache(): Promise<void> {
  if (_ready) return _ready;
  const factory = idbFactory();
  if (!factory) {
    _ready = Promise.resolve();
    return _ready;
  }
  _ready = new Promise<void>((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = factory.open(DB_NAME, DB_VERSION);
    } catch {
      resolve();
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    // A FAILED or BLOCKED open must be LOUD (2026-08 "stuck at 1%" class): when the
    // DB never opens, every putAudioBlob silently no-ops — a download run "fetches"
    // thousands of clips into a void and persists nothing. The service layer now
    // fails fast on that (cache-unavailable), and these logs are the device-truth
    // for support.
    req.onerror = () => {
      console.error('[audio-cache] IndexedDB open FAILED — downloads cannot persist:', req.error);
      resolve();
    };
    req.onblocked = () => {
      // Do NOT resolve here: onsuccess/onerror still fires once the blocker goes
      // away, and the caller-side deadline (contentPackService cache-unavailable)
      // covers a permanent block. Resolving now would report "ready" with no DB.
      console.error('[audio-cache] IndexedDB open BLOCKED by another connection — waiting…');
    };
    req.onsuccess = () => {
      _db = req.result;
      // Read keys + sizes (not blobs) so availability checks are cheap and sync.
      try {
        const tx = _db.transaction(STORE, 'readonly');
        const cursorReq = tx.objectStore(STORE).openCursor();
        cursorReq.onsuccess = () => {
          const cur = cursorReq.result;
          if (cur) {
            _keys.add(String(cur.key));
            _bytes += (cur.value as ClipRecord)?.size ?? 0;
            cur.continue();
          } else {
            _revision++;   // key set (re)loaded — derived counts must recompute
            resolve();
          }
        };
        cursorReq.onerror = () => {
          console.error('[audio-cache] IndexedDB key scan failed:', cursorReq.error);
          resolve();
        };
      } catch (e) {
        console.error('[audio-cache] IndexedDB key scan threw:', e);
        resolve();
      }
    };
  });
  return _ready;
}

/** Sync: is this key cached? (mirror of the DB key set) */
export function audioCacheHas(key: string): boolean {
  return _keys.has(key);
}

/** Sync: monotonic counter that changes whenever the cached KEY SET changes.
 *  Memoize expensive derived counts against this — equal revision ⇒ the cache
 *  contents are unchanged and the memo is still valid. */
export function audioCacheRevision(): number {
  return _revision;
}

/* ── PIN set (Phase 2) — LRU-eviction exemption ─────────────────────────────
 * The content-pack prefetcher pins the keys the child needs NOW (current grade
 * ±1 + the bundled floor). Pinned keys stay resident even under cache pressure,
 * so a large book download can never LRU-evict the current lesson's audio out
 * from under the child. Idempotent; unknown keys are harmless (they simply gain
 * protection if/when they are later cached). */
export function pinKeys(keys: Iterable<string>): void {
  for (const k of keys) _pinned.add(k);
}

/** Remove keys from the pin set (they become eligible for LRU eviction again). */
export function unpinKeys(keys: Iterable<string>): void {
  for (const k of keys) _pinned.delete(k);
}

/** Replace the entire pin set (e.g. on a grade change: pin only the new grade ±1
 *  + floor, releasing the old grade to reclaim space). */
export function setPinnedKeys(keys: Iterable<string>): void {
  _pinned.clear();
  for (const k of keys) _pinned.add(k);
}

/** Sync: is this key pinned (protected from eviction)? */
export function isKeyPinned(key: string): boolean {
  return _pinned.has(key);
}

/** Sync: number of pinned keys (diagnostics/tests). */
export function pinnedKeyCount(): number {
  return _pinned.size;
}

/** Sync: number of cached clips. Drives the speak() tier gate. */
export function audioCacheCount(): number {
  return _keys.size;
}

/** Read a cached clip's blob (and bump its LRU recency). null if absent. */
export async function getAudioBlob(key: string): Promise<Blob | null> {
  if (!_db || !_keys.has(key)) return null;
  return new Promise<Blob | null>((resolve) => {
    try {
      const tx = _db!.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const getReq = store.get(key);
      getReq.onsuccess = () => {
        const rec = getReq.result as ClipRecord | undefined;
        if (!rec?.blob) {
          resolve(null);
          return;
        }
        rec.lastUsed = Date.now();
        try {
          store.put(rec, key); // touch recency; ignore failure
        } catch {
          /* ignore */
        }
        resolve(rec.blob);
      };
      getReq.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Persist a clip. No-ops if absent DB, empty blob, or already cached. */
export async function putAudioBlob(key: string, blob: Blob): Promise<void> {
  if (!_db || !blob || !blob.size || _keys.has(key)) return;
  const size = blob.size;
  await new Promise<void>((resolve) => {
    try {
      const tx = _db!.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ blob, size, lastUsed: Date.now() } as ClipRecord, key);
      tx.oncomplete = () => {
        _keys.add(key);
        _bytes += size;
        _revision++;
        resolve();
      };
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
  if (_bytes > MAX_BYTES) void prune();
}

/** Snapshot every stored clip's key/size/lastUsed. */
async function readEntries(): Promise<{ key: string; size: number; lastUsed: number }[]> {
  const entries: { key: string; size: number; lastUsed: number }[] = [];
  if (!_db) return entries;
  await new Promise<void>((resolve) => {
    try {
      const tx = _db!.transaction(STORE, 'readonly');
      const cur = tx.objectStore(STORE).openCursor();
      cur.onsuccess = () => {
        const c = cur.result;
        if (c) {
          const v = c.value as ClipRecord;
          entries.push({ key: String(c.key), size: v?.size ?? 0, lastUsed: v?.lastUsed ?? 0 });
          c.continue();
        } else {
          resolve();
        }
      };
      cur.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
  return entries;
}

/** Delete a set of clips and update the in-memory mirrors. Never rejects. */
async function deleteClips(
  toDelete: string[],
  entries: { key: string; size: number }[],
): Promise<number> {
  if (!_db || !toDelete.length) return 0;
  const doomed = new Set(toDelete);
  let freed = 0;
  await new Promise<void>((resolve) => {
    try {
      const tx = _db!.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      for (const e of entries) {
        if (!doomed.has(e.key)) continue;
        store.delete(e.key);
        freed += e.size;
      }
      tx.oncomplete = () => {
        for (const k of doomed) _keys.delete(k);
        _bytes = Math.max(0, _bytes - freed);
        _revision++;
        resolve();
      };
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
  return freed;
}

/** Evict least-recently-used clips until back under ~90% of the cap. */
async function prune(): Promise<void> {
  if (!_db) return;
  const entries = await readEntries();
  entries.sort((a, b) => a.lastUsed - b.lastUsed); // oldest first
  const target = MAX_BYTES * 0.9;
  const toDelete: string[] = [];
  let projected = _bytes;
  for (const e of entries) {
    if (projected <= target) break;
    if (_pinned.has(e.key)) continue; // pinned (active level / mid-lesson) — never evict
    toDelete.push(e.key);
    projected -= e.size;
  }
  await deleteClips(toDelete, entries);
}

/**
 * Delete specific clips — the "remove this level from my device" action in Manage
 * Downloads (spec §6). PINNED keys are skipped: a level the parent explicitly keeps,
 * or the one the child is mid-lesson in, is never removed out from under them.
 *
 * Returns the number of clips actually deleted.
 */
export async function deleteAudioKeys(keys: Iterable<string>): Promise<number> {
  await initAudioCache();
  if (!_db) return 0;
  const wanted = new Set<string>();
  for (const k of keys) if (_keys.has(k) && !_pinned.has(k)) wanted.add(k);
  if (!wanted.size) return 0;
  const entries = await readEntries();
  const doomed = entries.filter((e) => wanted.has(e.key)).map((e) => e.key);
  if (!doomed.length) return 0;
  await deleteClips(doomed, entries);
  return doomed.length;
}

/** Total bytes currently held (Manage Downloads shows this). */
export function audioCacheBytes(): number {
  return _bytes;
}

/**
 * Auto-remove clips untouched for `maxAgeMs` (spec §6). Runs at startup, well under
 * the size cap, so a device isn't left holding a level the child moved on from
 * months ago. PINS ARE RESPECTED: a level the parent marked "keep on device", the
 * active level, and anything mid-lesson are never swept — and the bundled trial
 * floor isn't in this cache at all, so it can't be touched.
 *
 * Returns the number of clips removed (0 when the cache is unavailable).
 */
export async function sweepStaleClips(maxAgeMs: number = STALE_CLIP_MAX_AGE_MS): Promise<number> {
  await initAudioCache();
  if (!_db) return 0;
  const cutoff = Date.now() - Math.max(0, maxAgeMs);
  const entries = await readEntries();
  const stale = entries
    .filter((e) => !_pinned.has(e.key) && e.lastUsed > 0 && e.lastUsed < cutoff)
    .map((e) => e.key);
  if (!stale.length) return 0;
  await deleteClips(stale, entries);
  return stale.length;
}
